import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Package, DollarSign, Eye, Plus, ChevronRight, Clock, CheckCircle, Wallet, Star, Users, Gavel } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const { user, products, orders, offers, getUserOrders, getProductsBySeller } = useApp();
  const [pendingAuctionCount, setPendingAuctionCount] = React.useState(0);

  React.useEffect(() => {
    if (user?.id) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auctions/seller/pending?user_id=${user.id}`)
        .then(r => r.ok ? r.json() : [])
        .then(data => setPendingAuctionCount(data.length))
        .catch(() => {});
    }
  }, [user?.id]);

  const myListings = getProductsBySeller(user?.id);
  const myOrders = getUserOrders(true); // As seller
  const pendingOrders = myOrders.filter(o => o.status === 'pending' || o.status === 'pickup_arranged');
  const pendingOffers = offers.filter(o => o.sellerId === user?.id && o.status === 'pending');
  
  // Safely access wallet with defaults
  const wallet = {
    availableBalance: user?.wallet?.availableBalance ?? 0,
    pendingBalance: user?.wallet?.pendingBalance ?? 0,
    totalEarnings: user?.wallet?.totalEarnings ?? 0
  };
  
  // Calculate stats
  const totalViews = myListings.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalLikes = myListings.reduce((sum, p) => sum + (p.likes || 0), 0);

  return (
    <Layout showSearch={false}>
      {/* Seller Header */}
      <div className="bg-gradient-to-br from-minted-600 to-minted-700 px-4 py-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-minted-100 text-sm">Welcome back,</p>
            <h1 className="text-xl font-bold">{user?.name?.split(' ')[0]} 👋</h1>
          </div>
          <Badge className="bg-white/20 text-white">Seller Mode</Badge>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/20 rounded-lg p-3 text-center">
            <DollarSign className="w-5 h-5 mx-auto mb-1 text-minted-100" />
            <p className="text-lg font-bold">${wallet.availableBalance.toFixed(0)}</p>
            <p className="text-[10px] text-minted-100">Available</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3 text-center">
            <Clock className="w-5 h-5 mx-auto mb-1 text-minted-100" />
            <p className="text-lg font-bold">${wallet.pendingBalance.toFixed(0)}</p>
            <p className="text-[10px] text-minted-100">Pending</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3 text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-minted-100" />
            <p className="text-lg font-bold">${wallet.totalEarnings.toFixed(0)}</p>
            <p className="text-[10px] text-minted-100">Total Sales</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mt-4">
          <Button
            onClick={() => navigate('/sell')}
            className="flex-1 bg-white text-minted-600 hover:bg-minted-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Listing
          </Button>
          <Button
            onClick={() => navigate('/wallet')}
            variant="outline"
            className="flex-1 border-white/50 text-white hover:bg-white/20"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Withdraw
          </Button>
        </div>
      </div>

      {/* Action Items */}
      {(pendingOrders.length > 0 || pendingOffers.length > 0 || pendingAuctionCount > 0) && (
        <div className="px-4 py-4 bg-yellow-50 border-b border-yellow-100">
          <h3 className="font-semibold text-yellow-900 mb-2">Action Required</h3>
          <div className="space-y-2">
            {pendingAuctionCount > 0 && (
              <button
                onClick={() => navigate('/seller/auction-requests')}
                className="w-full flex items-center justify-between bg-white p-3 rounded-lg shadow-sm"
                data-testid="seller-auction-requests-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Gavel className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{pendingAuctionCount} auction request{pendingAuctionCount !== 1 ? 's' : ''}</p>
                    <p className="text-xs text-gray-500">Review and approve</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            )}
            {pendingOrders.length > 0 && (
              <button
                onClick={() => navigate('/orders')}
                className="w-full flex items-center justify-between bg-white p-3 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Package className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{pendingOrders.length} orders to ship</p>
                    <p className="text-xs text-gray-500">Add tracking info</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            )}
            {pendingOffers.length > 0 && (
              <button
                onClick={() => navigate('/offers')}
                className="w-full flex items-center justify-between bg-white p-3 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{pendingOffers.length} new offers</p>
                    <p className="text-xs text-gray-500">Review and respond</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Listing Performance */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Your Listings</h3>
          <button
            onClick={() => navigate('/my-listings')}
            className="text-minted-600 text-sm font-medium flex items-center gap-1"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Performance Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{myListings.length}</p>
            <p className="text-[10px] text-gray-500">Active</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{totalViews}</p>
            <p className="text-[10px] text-gray-500">Views</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{totalLikes}</p>
            <p className="text-[10px] text-gray-500">Likes</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold text-gray-900">{user?.salesCount || 0}</p>
            <p className="text-[10px] text-gray-500">Sold</p>
          </div>
        </div>

        {/* Recent Listings */}
        {myListings.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm mb-3">No active listings yet</p>
            <Button onClick={() => navigate('/sell')} className="bg-minted-600 hover:bg-minted-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Listing
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {myListings.slice(0, 3).map(product => (
              <button
                key={product.id}
                onClick={() => navigate(`/product/${product.id}`)}
                className="w-full flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="w-14 h-14 rounded-lg object-cover"
                />
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900 text-sm line-clamp-1">{product.title}</p>
                  <p className="text-minted-600 font-bold">${product.price}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <Eye className="w-3 h-3" />
                    {product.views}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Seller Tools */}
      <div className="px-4 py-4 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-3">Seller Tools</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/orders')}
            className="bg-white p-4 rounded-lg shadow-sm text-left hover:shadow-md transition-shadow"
          >
            <Package className="w-6 h-6 text-blue-600 mb-2" />
            <p className="font-medium text-gray-900">Orders</p>
            <p className="text-xs text-gray-500">Manage shipments</p>
          </button>
          <button
            onClick={() => navigate('/messages')}
            className="bg-white p-4 rounded-lg shadow-sm text-left hover:shadow-md transition-shadow"
          >
            <Users className="w-6 h-6 text-purple-600 mb-2" />
            <p className="font-medium text-gray-900">Buyers</p>
            <p className="text-xs text-gray-500">Chat with buyers</p>
          </button>
          <button
            onClick={() => navigate('/invite')}
            className="bg-white p-4 rounded-lg shadow-sm text-left hover:shadow-md transition-shadow"
          >
            <Star className="w-6 h-6 text-yellow-600 mb-2" />
            <p className="font-medium text-gray-900">Earn More</p>
            <p className="text-xs text-gray-500">Invite friends</p>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="bg-white p-4 rounded-lg shadow-sm text-left hover:shadow-md transition-shadow"
          >
            <CheckCircle className="w-6 h-6 text-minted-600 mb-2" />
            <p className="font-medium text-gray-900">Reviews</p>
            <p className="text-xs text-gray-500">{user?.reviewCount || 0} reviews</p>
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default SellerDashboard;
